/**
 * The Sun's position, solar noon, sunrise and sunset — the NOAA algorithm.
 *
 * This is the calculation behind NOAA's Solar Calculator spreadsheet
 * (https://gml.noaa.gov/grad/solcalc/calcdetails.html), which NOAA describes as based on
 * Jean Meeus, Astronomical Algorithms (2nd ed., 1998), chapters 7, 12, 22, 25 and 28: the
 * Sun's geometric mean longitude and anomaly, the equation of the centre, the apparent
 * longitude, the corrected obliquity, the declination and the equation of time. NOAA gives
 * sunrise and sunset as accurate to within a minute between 72° N and 72° S and within ten
 * minutes beyond that.
 *
 * Conventions, all NOAA's:
 * - longitude is positive east of Greenwich, latitude positive north;
 * - sunrise and sunset use a zenith angle of 90.833° (the Sun's upper limb, 0.2666°, plus
 *   0.5667° of standard atmospheric refraction), i.e. the Sun's centre at −0.833°;
 * - elevation is reported with Bennett-style refraction, as NOAA's spreadsheet does,
 *   and the uncorrected geometric elevation is returned beside it;
 * - azimuth is measured clockwise from true north.
 */

const RAD = Math.PI / 180;
const DEG = 180 / Math.PI;
const MS_PER_DAY = 86_400_000;
const UNIX_EPOCH_JD = 2_440_587.5;
const J2000 = 2_451_545;

/** The zenith angle of sunrise and sunset, degrees. */
export const SUNRISE_ZENITH = 90.833;

export function julianDay(ms: number): number {
  return ms / MS_PER_DAY + UNIX_EPOCH_JD;
}

export function instantFromJulianDay(jd: number): number {
  return (jd - UNIX_EPOCH_JD) * MS_PER_DAY;
}

function mod(value: number, divisor: number): number {
  return ((value % divisor) + divisor) % divisor;
}

export interface SunCoordinates {
  /** Declination, degrees. */
  declination: number;
  /** Equation of time, minutes (apparent minus mean solar time). */
  equationOfTime: number;
}

/** Declination and equation of time at a Julian day. */
export function sunCoordinates(jd: number): SunCoordinates {
  const t = (jd - J2000) / 36_525;
  const meanLongitude = mod(
    280.466_46 + t * (36_000.769_83 + t * 0.000_303_2),
    360
  );
  const meanAnomaly = 357.529_11 + t * (35_999.050_29 - 0.000_153_7 * t);
  const eccentricity =
    0.016_708_634 - t * (0.000_042_037 + 0.000_000_126_7 * t);
  const m = meanAnomaly * RAD;
  const centre =
    Math.sin(m) * (1.914_602 - t * (0.004_817 + 0.000_014 * t)) +
    Math.sin(2 * m) * (0.019_993 - 0.000_101 * t) +
    Math.sin(3 * m) * 0.000_289;
  const trueLongitude = meanLongitude + centre;
  const omega = (125.04 - 1934.136 * t) * RAD;
  const apparentLongitude =
    trueLongitude - 0.005_69 - 0.004_78 * Math.sin(omega);
  const meanObliquity =
    23 +
    (26 + (21.448 - t * (46.815 + t * (0.000_59 - t * 0.001_813))) / 60) / 60;
  const obliquity = meanObliquity + 0.002_56 * Math.cos(omega);
  const declination =
    Math.asin(Math.sin(obliquity * RAD) * Math.sin(apparentLongitude * RAD)) *
    DEG;
  const y = Math.tan((obliquity * RAD) / 2) ** 2;
  const l0 = meanLongitude * RAD;
  const equationOfTime =
    4 *
    DEG *
    (y * Math.sin(2 * l0) -
      2 * eccentricity * Math.sin(m) +
      4 * eccentricity * y * Math.sin(m) * Math.cos(2 * l0) -
      0.5 * y * y * Math.sin(4 * l0) -
      1.25 * eccentricity * eccentricity * Math.sin(2 * m));
  return { declination, equationOfTime };
}

/** Atmospheric refraction in degrees for a geometric elevation (NOAA's approximation). */
export function refractionDegrees(elevation: number): number {
  if (elevation > 85) {
    return 0;
  }
  const tanE = Math.tan(elevation * RAD);
  let arcseconds: number;
  if (elevation > 5) {
    arcseconds = 58.1 / tanE - 0.07 / tanE ** 3 + 0.000_086 / tanE ** 5;
  } else if (elevation > -0.575) {
    arcseconds =
      1735 +
      elevation *
        (-518.2 +
          elevation * (103.4 + elevation * (-12.79 + elevation * 0.711)));
  } else {
    arcseconds = -20.772 / tanE;
  }
  return arcseconds / 3600;
}

export interface SunPosition {
  /** Degrees above the horizon, with refraction. */
  elevation: number;
  /** Degrees above the horizon, geometric (no refraction). */
  geometricElevation: number;
  /** Degrees from the zenith, with refraction. */
  zenith: number;
  /** Degrees clockwise from true north. */
  azimuth: number;
  declination: number;
  equationOfTime: number;
  /** Hour angle in degrees, negative before solar noon. */
  hourAngle: number;
  /** True (apparent) solar time, minutes after midnight. */
  trueSolarTime: number;
  refraction: number;
}

function clampUnit(value: number): number {
  return Math.min(1, Math.max(-1, value));
}

/** The Sun's position for a place and a UTC instant (milliseconds since the epoch). */
export function sunPosition(
  latitude: number,
  longitude: number,
  ms: number
): SunPosition {
  const { declination, equationOfTime } = sunCoordinates(julianDay(ms));
  const minutesUtc = mod(ms / 60_000, 1440);
  const trueSolarTime = mod(minutesUtc + equationOfTime + 4 * longitude, 1440);
  const hourAngle = trueSolarTime / 4 - 180;
  const lat = latitude * RAD;
  const dec = declination * RAD;
  const cosZenith = clampUnit(
    Math.sin(lat) * Math.sin(dec) +
      Math.cos(lat) * Math.cos(dec) * Math.cos(hourAngle * RAD)
  );
  const zenithGeometric = Math.acos(cosZenith) * DEG;
  const geometricElevation = 90 - zenithGeometric;
  const refraction = refractionDegrees(geometricElevation);
  const sinZenith = Math.sin(zenithGeometric * RAD);
  const denominator = Math.cos(lat) * sinZenith;
  let azimuth = 180;
  if (Math.abs(denominator) > 1e-9) {
    const base =
      Math.acos(
        clampUnit((Math.sin(lat) * cosZenith - Math.sin(dec)) / denominator)
      ) * DEG;
    azimuth = hourAngle > 0 ? mod(base + 180, 360) : mod(540 - base, 360);
  }
  return {
    elevation: geometricElevation + refraction,
    geometricElevation,
    zenith: 90 - (geometricElevation + refraction),
    azimuth,
    declination,
    equationOfTime,
    hourAngle,
    trueSolarTime,
    refraction,
  };
}

export type DayStatus = "normal" | "polar-day" | "polar-night";

/** cos of the sunrise hour angle for a latitude and declination (degrees). */
function cosSunriseHourAngle(latitude: number, declination: number): number {
  const lat = latitude * RAD;
  const dec = declination * RAD;
  return (
    Math.cos(SUNRISE_ZENITH * RAD) / (Math.cos(lat) * Math.cos(dec)) -
    Math.tan(lat) * Math.tan(dec)
  );
}

export function dayStatusFor(latitude: number, declination: number): DayStatus {
  const c = cosSunriseHourAngle(latitude, declination);
  if (c > 1) {
    return "polar-night";
  }
  if (c < -1) {
    return "polar-day";
  }
  return "normal";
}

export interface SolarDay {
  /** UTC instant of solar noon (the Sun due south or north). */
  noon: number;
  /** Sunrise and sunset instants; null in polar day or polar night. */
  sunrise: number | null;
  sunset: number | null;
  status: DayStatus;
  /** Seconds of daylight (0 in polar night, 86 400 in polar day). */
  daylightSeconds: number;
  /** Elevation of the Sun at solar noon, with refraction, degrees. */
  noonElevation: number;
  declination: number;
  equationOfTime: number;
}

/** UTC midnight at the start of a UTC day number, as a Julian day. */
function dayStartJd(utcDay: number): number {
  return utcDay + UNIX_EPOCH_JD;
}

/**
 * Solar noon, sunrise and sunset for the solar day that belongs to a UTC date.
 *
 * "Belongs to" means the day whose solar noon falls within ±12 h of that date's
 * `720 − 4·longitude` minute mark; callers pick the one that lands on the calendar day
 * they want in their own time zone. Each time is refined three times, evaluating the
 * declination and equation of time at the previous estimate, as NOAA's web calculator does.
 */
export function solarDayForUtcDate(
  latitude: number,
  longitude: number,
  utcDay: number
): SolarDay {
  const base = dayStartJd(utcDay);
  const at = (minutes: number): SunCoordinates =>
    sunCoordinates(base + minutes / 1440);
  let noonMin = 720 - 4 * longitude;
  for (let pass = 0; pass < 3; pass += 1) {
    noonMin = 720 - 4 * longitude - at(noonMin).equationOfTime;
  }
  const noonCoords = at(noonMin);
  const status = dayStatusFor(latitude, noonCoords.declination);
  const noon = instantFromJulianDay(base + noonMin / 1440);
  const noonElevation = sunPosition(latitude, longitude, noon).elevation;
  const common = {
    noon,
    status,
    noonElevation,
    declination: noonCoords.declination,
    equationOfTime: noonCoords.equationOfTime,
  };
  if (status !== "normal") {
    return {
      ...common,
      sunrise: null,
      sunset: null,
      daylightSeconds: status === "polar-day" ? 86_400 : 0,
    };
  }
  const event = (sign: 1 | -1): number => {
    let minutes = noonMin;
    for (let pass = 0; pass < 3; pass += 1) {
      const coords = at(minutes);
      const c = clampUnit(cosSunriseHourAngle(latitude, coords.declination));
      const hourAngle = Math.acos(c) * DEG;
      minutes =
        720 - 4 * (longitude + sign * hourAngle) - coords.equationOfTime;
    }
    return instantFromJulianDay(base + minutes / 1440);
  };
  const sunrise = event(1);
  const sunset = event(-1);
  return {
    ...common,
    sunrise,
    sunset,
    daylightSeconds: (sunset - sunrise) / 1000,
  };
}

/** Day number (days since 1970-01-01, UTC) containing an instant. */
export function utcDayOf(ms: number): number {
  return Math.floor(ms / MS_PER_DAY);
}

/**
 * The solar day whose local calendar date is `localDay` in a zone.
 *
 * `localDateOf` maps an instant to the zone's day number, so this module needs no time-zone
 * code of its own. Candidates are the UTC dates around `localDay`; if none of their noons
 * lands on it (a zone far from its solar time on a daylight-saving day), the nearest wins.
 */
export function solarDayOnLocalDate(
  latitude: number,
  longitude: number,
  localDay: number,
  localDateOf: (ms: number) => number
): SolarDay {
  let best: SolarDay | null = null;
  let bestGap = Number.POSITIVE_INFINITY;
  for (const offset of [0, -1, 1]) {
    const candidate = solarDayForUtcDate(
      latitude,
      longitude,
      localDay + offset
    );
    const gap = Math.abs(localDateOf(candidate.noon) - localDay);
    if (gap < bestGap) {
      best = candidate;
      bestGap = gap;
    }
  }
  return best ?? solarDayForUtcDate(latitude, longitude, localDay);
}

/** Length of the shadow of a vertical object, as a multiple of its height (null when the Sun is down). */
export function shadowRatio(elevation: number): number | null {
  if (elevation <= 0) {
    return null;
  }
  return 1 / Math.tan(elevation * RAD);
}

/** Compass point for an azimuth, in 16 winds: 0 → "N", 90 → "E". */
export const COMPASS_POINTS = [
  "N",
  "NNE",
  "NE",
  "ENE",
  "E",
  "ESE",
  "SE",
  "SSE",
  "S",
  "SSW",
  "SW",
  "WSW",
  "W",
  "WNW",
  "NW",
  "NNW",
] as const;

export function compassPoint(azimuth: number): (typeof COMPASS_POINTS)[number] {
  return COMPASS_POINTS[Math.round(mod(azimuth, 360) / 22.5) % 16];
}

/**
 * Solar noon elevation without refraction: 90° − |latitude − declination|.
 */
export function noonElevationFormula(
  latitude: number,
  declination: number
): number {
  return 90 - Math.abs(latitude - declination);
}

/**
 * The refracted elevation every `stepMinutes` for `count + 1` points from `startMs`;
 * x is minutes after the start. Used to draw the Sun's path through a day.
 */
export function elevationSeries(
  latitude: number,
  longitude: number,
  startMs: number,
  stepMinutes: number,
  count: number
): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i <= count; i += 1) {
    const minutes = i * stepMinutes;
    out.push([
      minutes,
      sunPosition(latitude, longitude, startMs + minutes * 60_000).elevation,
    ]);
  }
  return out;
}

/** Hours of daylight on each day from `firstUtcDay`, for `days` days (a year chart). */
export function daylightSeries(
  latitude: number,
  longitude: number,
  firstUtcDay: number,
  days: number
): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i < days; i += 1) {
    const day = solarDayForUtcDate(latitude, longitude, firstUtcDay + i);
    out.push([i, day.daylightSeconds / 3600]);
  }
  return out;
}

export type TwilightPhase =
  | "day"
  | "civil"
  | "nautical"
  | "astronomical"
  | "night";

/**
 * Where the Sun's centre sits relative to the standard thresholds: above −0.833° is day,
 * then civil (to −6°), nautical (to −12°) and astronomical (to −18°) twilight, then night.
 * Uses the geometric elevation, as the definitions do.
 */
export function twilightPhase(geometricElevation: number): TwilightPhase {
  if (geometricElevation > -0.833) {
    return "day";
  }
  if (geometricElevation > -6) {
    return "civil";
  }
  if (geometricElevation > -12) {
    return "nautical";
  }
  if (geometricElevation > -18) {
    return "astronomical";
  }
  return "night";
}
